/**
 * Service for handling file uploads
 */

import api from './api';

/**
 * Upload class with images
 * @param {Object} classData - Class data
 * @param {Array} images - Array of image files
 * @returns {Promise} - API response
 */
export const uploadClassWithImages = async (classData, images) => {
  const formData = new FormData();
  
  // Process prepayment field
  // If workshop is 'yes' and requires_prepayment is true, include prepayment
  // Otherwise set it to null to avoid backend errors
  const processedData = {
    ...classData,
    prepayment: classData.workshop === 'yes' && classData.requires_prepayment ? classData.prepayment : null
  };
  
  console.log('Processed class data for upload:', processedData);
  
  // Add class data as JSON
  formData.append('data', JSON.stringify(processedData));
  
  // Add images
  if (images && images.length) {
    images.forEach(image => {
      formData.append('images', image);
    });
  }
  
  // Set headers for multipart/form-data
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  
  // Determine if this is an update or create
  if (classData.id) {
    return api.put(`classes/${classData.id}`, formData, config);
  } else {
    return api.post('classes', formData, config);
  }
};

/**
 * Update class with new images, appending or replacing existing ones
 * @param {number} classId - Class ID
 * @param {Object} classData - Class data
 * @param {Array} images - Array of image files
 * @param {boolean} replaceImages - Whether to replace existing images
 * @returns {Promise} - API response
 */
export const updateClassWithImages = async (classId, classData, images, replaceImages = false) => {
  const formData = new FormData();
  
  // Add class data and ensure the replace_images flag is set
  // We want to replace images if either:
  // 1. The replaceImages param is true, or
  // 2. The classData.replace_images is true
  // Also process prepayment field
  const dataWithReplaceFlag = {
    ...classData,
    replace_images: classData.replace_images || replaceImages,
    prepayment: classData.workshop === 'yes' && classData.requires_prepayment ? classData.prepayment : null
  };
  
  console.log('Final replace_images flag:', dataWithReplaceFlag.replace_images);
  console.log('Prepayment value for update:', dataWithReplaceFlag.prepayment);
  
  formData.append('data', JSON.stringify(dataWithReplaceFlag));
  
  // Add images
  if (images && images.length) {
    images.forEach(image => {
      formData.append('images', image);
    });
  }
  
  // Set headers for multipart/form-data
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  
  return api.put(`classes/${classId}`, formData, config);
};

/**
 * Upload/update instructor with gallery images, with option to replace existing ones
 * @param {Object} instructorData - Instructor data
 * @param {Array} galleryImages - Array of gallery image files
 * @param {boolean} replaceImages - Whether to replace existing gallery images
 * @returns {Promise} - API response
 */
export const updateInstructorWithGalleryImages = async (instructorData, galleryImages, replaceImages = false) => {
  const formData = new FormData();
  
  // Convert boolean to ensure it's properly formatted for server
  const replaceImagesValue = replaceImages === true;
  
  // Add instructor data with replace_images flag
  const dataWithReplaceFlag = {
    ...instructorData,
    replace_images: replaceImagesValue
  };
  
  console.log('Instructor gallery replace_images flag:', dataWithReplaceFlag.replace_images);
  console.log('Instructor data being sent:', dataWithReplaceFlag);
  console.log('Instructor ID:', instructorData.id, typeof instructorData.id);
  
  formData.append('data', JSON.stringify(dataWithReplaceFlag));
  
  // Add gallery images
  if (galleryImages && galleryImages.length) {
    console.log(`Adding ${galleryImages.length} gallery images to form data`);
    galleryImages.forEach(image => {
      formData.append('gallery_images', image);
    });
  }
  
  // Set headers for multipart/form-data
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  
  // Determine if this is an update or create
  if (instructorData.id) {
    console.log(`Making PUT request to instructors/${instructorData.id}`);
    return api.put(`instructors/${instructorData.id}`, formData, config);
  } else {
    console.log('Making POST request to instructors (no ID found)');
    return api.post('instructors', formData, config);
  }
};

export default {
  uploadClassWithImages,
  updateClassWithImages,
  updateInstructorWithGalleryImages
}; 